import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Activity, Loader2, Mail, RefreshCw } from "lucide-react";
import { emailSystemsCheck, getSystems, runSystemsCheck } from "@/lib/api";

// Systems check (backend routes/systems_check.py): every bot, background job, ad account and paid balance FPA
// depends on. Runs hourly; the morning report is emailed, and a check that newly fails sends one alert a day.

const err = (e, fallback) => e?.response?.data?.detail || e?.message || fallback;
const STYLE = {
  fail: { word: "Failing", dot: "bg-red-500", text: "text-red-400" },
  warn: { word: "Look at", dot: "bg-amber-400", text: "text-amber-300" },
  ok: { word: "OK", dot: "bg-emerald-500", text: "text-emerald-400" },
  off: { word: "Off", dot: "bg-zinc-600", text: "text-zinc-500" },
};
const GROUPS = ["Ads", "Bots", "Services", "Jobs"];

export default function Systems() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    getSystems().then(setData).catch((e) => toast.error(err(e, "Couldn't load the systems check")));
  }, []);
  useEffect(() => { load(); }, [load]);

  const runNow = async () => {
    setBusy("run");
    try { const r = await runSystemsCheck(); setData((d) => ({ ...d, run: r.run })); } catch (e) { toast.error(err(e, "Check failed")); } finally { setBusy(""); }
  };
  const emailNow = async () => {
    setBusy("email");
    try {
      const r = await emailSystemsCheck();
      const sent = (r.results || []).filter((x) => x.sent).length;
      sent ? toast.success(`Emailed to ${sent} ${sent === 1 ? "person" : "people"}`) : toast.error((r.results || [])[0]?.reason || "Not sent");
      load();
    } catch (e) { toast.error(err(e, "Couldn't email it")); } finally { setBusy(""); }
  };

  const run = data?.run;
  const c = run?.counts || {};
  return (
    <div className="space-y-6 max-w-4xl" data-testid="systems-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="eyebrow inline-flex items-center gap-1.5"><Activity className="h-3.5 w-3.5" />Across every gym</div>
          <h2 className="font-display text-3xl font-extrabold mt-1 tracking-tight">Systems</h2>
          <p className="text-sm text-zinc-400 mt-1">
            Bots, background jobs, ad accounts and balances. Checked every hour; the morning report is emailed to{" "}
            {(data?.to || []).join(", ") || "nobody"}{data && !data.emails_on ? " (emails off)" : ""}.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={emailNow} disabled={!!busy}
            className="h-9 px-3 rounded-full border border-[#2a2a2a] text-sm text-zinc-200 inline-flex items-center gap-1.5 disabled:opacity-50">
            {busy === "email" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />}Email report
          </button>
          <button onClick={runNow} disabled={!!busy} data-testid="systems-run"
            className="h-9 px-3 rounded-full border border-[#2a2a2a] text-sm text-zinc-200 inline-flex items-center gap-1.5 disabled:opacity-50">
            {busy === "run" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}Check now
          </button>
        </div>
      </div>

      {!data && <div className="text-zinc-500 text-sm inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Loading</div>}
      {data && !run && <div className="text-zinc-500 text-sm">No check has run yet. Press Check now.</div>}

      {run && (
        <>
          <div className="text-sm text-zinc-400">
            Checked {new Date(run.at).toLocaleString()} ·{" "}
            <span className="text-red-400">{c.fail || 0} failing</span> ·{" "}
            <span className="text-amber-300">{c.warn || 0} to look at</span> ·{" "}
            <span className="text-emerald-400">{c.ok || 0} OK</span>
            {c.off ? <span className="text-zinc-500"> · {c.off} switched off</span> : null}
          </div>
          {GROUPS.map((g) => {
            const rows = run.checks.filter((r) => r.group === g);
            if (!rows.length) return null;
            return (
              <section key={g} className="card-surface p-4">
                <div className="text-[11px] uppercase tracking-[0.16em] text-zinc-500 mb-2">{g}</div>
                <div className="divide-y divide-[#1a1a1a]">
                  {rows.map((r) => (
                    <div key={r.key} className="py-2.5 flex gap-3 items-start" data-testid="systems-check">
                      <span className={`mt-1.5 h-2 w-2 rounded-full shrink-0 ${STYLE[r.status].dot}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-baseline gap-x-2">
                          <span className="text-zinc-100 font-medium">{r.label}</span>
                          <span className={`text-xs ${STYLE[r.status].text}`}>{STYLE[r.status].word}</span>
                        </div>
                        <div className="text-sm text-zinc-400 break-words">{r.summary}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            );
          })}
        </>
      )}
    </div>
  );
}
